import classNames from 'clsx'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import './OneTimePasswordInput.scss'

type OneTimePasswordInputProps = {
  length?: number
  defaultValue?: string
  name?: string
  hasError?: boolean
}

const OneTimePasswordInput = forwardRef<
  HTMLInputElement,
  OneTimePasswordInputProps
>(function OneTimePasswordInput(
  { length = 6, defaultValue = '', name = 'otp', hasError = false },
  ref
) {
  const [otp, setOtp] = useState<string[]>(new Array(length).fill(''))
  const inputs = useRef<(HTMLInputElement | null)[]>(
    new Array(length).fill(null)
  )
  const [showError, setShowError] = useState(true)

  useEffect(() => {
    if (hasError) {
      setOtp(new Array(length).fill(''))
      setShowError(true)
    }
  }, [hasError, length, setShowError])

  const focusNextInput = useCallback(
    (index: number) => {
      if (index + 1 < length) {
        inputs.current[index + 1]?.focus()
      } else if (index + 1 === length) {
        inputs.current[length - 1]?.focus()
      }
    },
    [length]
  )

  useEffect(() => {
    const defaultValueChars = defaultValue.split('')
    const isDefaultValueValidOneTimePassword =
      defaultValueChars.every(char => !isNaN(Number(char))) &&
      defaultValueChars.length === length

    if (isDefaultValueValidOneTimePassword) {
      const defaultDigits = defaultValueChars
      setOtp(defaultDigits)
      inputs.current[length - 1]?.focus()
    } else {
      setOtp(new Array(length).fill(''))
      focusNextInput(-1)
    }
  }, [defaultValue, length, focusNextInput])

  const handleChange = (element: HTMLInputElement, index: number) => {
    if (isNaN(Number(element.value))) return
    const newOtp = [...otp]
    newOtp[index] = element.value
    setOtp(newOtp)
    if (element.value && index < length - 1) {
      focusNextInput(index)
    }
    setShowError(false)
  }

  const handleKeyUp = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (event.key === 'Backspace') {
      if (otp[index] === '') {
        if (index !== 0) {
          const previousSibling = (event.target as HTMLInputElement)
            .previousSibling as HTMLInputElement | null
          previousSibling?.focus()
        }
      } else {
        const newOtp = [...otp]
        newOtp[index] = ''
        setOtp(newOtp)
      }
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pasteData = event.clipboardData
      ?.getData('text')
      .split('')
      .filter(char => !isNaN(Number(char)))
      .slice(0, length)

    const newOtp = [...otp]
    pasteData?.forEach((char, index) => {
      newOtp[index] = char
    })

    setOtp(newOtp)
    focusNextInput(pasteData?.length - 1)
    setShowError(false)
  }

  const inputClasses = classNames('o-OneTimePasswordInput__input', {
    'o-OneTimePasswordInput__input--error': hasError && showError,
  })

  return (
    <div className="l-flex l-flex--distribute-space">
      {otp.map((data, index) => (
        <input
          key={index}
          type="text"
          maxLength={1}
          value={data}
          onChange={e => handleChange(e.target as HTMLInputElement, index)}
          onKeyUp={e => handleKeyUp(e, index)}
          onPaste={handlePaste}
          ref={el => (inputs.current[index] = el)}
          className={inputClasses}
          data-qa-id={`otp-input-${index}`}
        />
      ))}
      <input type="hidden" name={name} value={otp.join('')} ref={ref} />
    </div>
  )
})

export default OneTimePasswordInput
